"use client";

import React, { useEffect, useState } from "react";
import { Clock, Building2, Scale, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface AnimatedCounterProps {
  end: number;
  suffix?: string;
  isVisible: boolean;
}
function AnimatedCounter({
  end,
  suffix = "",
  isVisible
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, isVisible]);
  return <span className="font-sans text-6xl font-bold tracking-tighter text-[#2dbd7e] drop-shadow-md">
      {count}
      {suffix}
    </span>;
}
export default function IndustryFootprint() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const metrics = [{
    id: 1,
    number: 10,
    suffix: "+",
    label: "Years Expertise",
    icon: Clock
  }, {
    id: 2,
    number: 30,
    suffix: "%",
    label: "Cost Reduction",
    icon: Building2
  }, {
    id: 3,
    number: 25,
    suffix: "%",
    label: "Revenue Growth",
    icon: Scale
  }, {
    id: 4,
    number: 500,
    suffix: "+",
    label: "Projects Completed",
    icon: Globe
  }];
  return <section data-section-id="153398" id="the-lavers-advantage" className="relative w-full overflow-hidden bg-[#1a1f26] py-24 text-white">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
    }}></div>
      
      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-sans text-4xl font-bold tracking-tight text-white sm:text-5xl">
            The Lavers Advantage
          </h2>
          <p className="mt-4 text-slate-400 font-sans max-w-2xl mx-auto">
            Leveraging over a decade of consulting experience to drive efficiency, maximize revenue, and deliver sustainable business growth.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map((metric, i) => {
          const Icon = metric.icon;
          return <div key={metric.id} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="group relative h-full overflow-hidden border-0 bg-transparent rounded-none lg:border-r border-slate-800 last:border-r-0 transition-all duration-300 hover:bg-white/5">
                  <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                    <div className="mb-6 rounded-none bg-transparent p-4 text-[#2dbd7e] transition-colors duration-300 group-hover:scale-110">
                      <Icon className="size-10" strokeWidth={1} />
                    </div>
                    
                    <div className="mb-4 flex flex-col items-center justify-center">
                      <AnimatedCounter end={metric.number} suffix={metric.suffix} isVisible={isVisible} />
                      <div className="mt-3 h-0.5 w-12 bg-[#2dbd7e] opacity-80" />
                    </div>
                    
                    <p className="font-mono text-xs font-bold uppercase tracking-widest text-slate-400">
                      {metric.label}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}